import React, { Suspense, useMemo } from 'react'
import { CircularProgress, Box, Typography } from '@mui/material'
import {
  CustomCreateOption,
  getLabelForAlreadyPatientOption,
  PatientSearchCustomOption,
} from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingComponents'
import { CustomAsyncCreatablePaginate } from 'src/consumer/components/helperComponents/dynamic'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { PATIENT_SEARCH_STYLES } from '../utils/patientFormConfig'

// Loading indicator component for pagination
const LoadingMessage = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '10px 12px',
      gap: '8px',
      margin: '4px 0',
      borderRadius: '8px',
      backgroundColor: 'rgba(67, 56, 202, 0.04)',
      border: '1px dashed rgba(67, 56, 202, 0.2)',
    }}
  >
    <CircularProgress size={14} thickness={5} sx={{ color: '#4338ca' }} />
    <Typography variant="caption" sx={{ color: '#4338ca', fontSize: '11px', fontWeight: 600 }}>
      Loading more patients...
    </Typography>
  </Box>
)

// Stable component identities: react-select remounts its menu whenever the `components`
// object (or any entry) changes, so these must not be re-created per render.
const OptionComponent = ({ children, ...props }) => {
  if (props.data.__isNew__) {
    return <CustomCreateOption {...props} />
  }
  return <PatientSearchCustomOption {...props} />
}

const MenuListWithLoader = ({ children, selectProps, ...props }) => (
  <div {...props}>
    {children}
    {selectProps?.isPaginationLoading && <LoadingMessage />}
  </div>
)

const SELECT_COMPONENTS = { Option: OptionComponent, MenuList: MenuListWithLoader }

const shouldLoadMore = (scrollHeight, clientHeight, scrollTop) => {
  // Only load more when scrolled to the very bottom (within 50px)
  const threshold = 50
  return scrollHeight - clientHeight - scrollTop < threshold
}

const reduceOptions = (prevOptions, loadedOptions) => {
  // Reduce options to prevent duplicates
  const existingIds = new Set(prevOptions.map((opt) => opt.id))
  const newOptions = loadedOptions.filter((opt) => !existingIds.has(opt.id))
  return [...prevOptions, ...newOptions]
}

const getOptionValue = (option) => option.id

export const PatientSearchDropdown = ({
  searchHook,
  disabled = false,
  clinicId,
  clinicBrandId,
  patientSearchApiSignature = '/api/secure/mentor/patient/list',
}) => {
  const {
    selectRef,
    inputRef,
    loadedPatientOptions,
    isPaginationLoading,
    loadOptionsForPatients,
    handlePatientSelect,
    handleInputChange,
    handleSelectFocus,
    handleSelectBlur,
    shouldShowSearchDropdown,
    getSearchConfig,
    formatCreateLabel,
    isValidNewOption,
    onCreateOption,
  } = searchHook

  const hasOptions = loadedPatientOptions?.length > 0
  const styles = useMemo(
    () => ({
      ...PATIENT_SEARCH_STYLES,
      menu: (base, state) => ({
        ...PATIENT_SEARCH_STYLES.menu(base, state),
        display: hasOptions ? 'block' : 'none',
      }),
      menuPortal: (base) => ({
        ...PATIENT_SEARCH_STYLES.menuPortal(base),
      }),
    }),
    [hasOptions],
  )

  const additional = useMemo(
    () => ({
      page: 1, // Backend API expects page numbers starting from 1
      clinicId,
      clinicBrandId,
      mentorAuthorisedToSaveAndGetPatientsUsingIdb: getAllRolesInBoolean().isDoctor,
      patientSearchApiSignature,
    }),
    [clinicId, clinicBrandId, patientSearchApiSignature],
  )

  if (!shouldShowSearchDropdown() || disabled) {
    return null
  }

  const config = getSearchConfig()

  return (
    <div style={{ position: 'absolute', zIndex: 1000, width: '100%', maxWidth: '450px' }}>
      <Suspense fallback={<LoadingMessage />}>
        <CustomAsyncCreatablePaginate
          selectRef={selectRef}
          cacheOptions={false}
          defaultOptions
          inputValue={config.inputValue}
          onInputChange={(value) => {
            handleInputChange(value, inputRef)
          }}
          menuIsOpen
          value={null}
          isClearable={true}
          onFocus={handleSelectFocus}
          onBlur={handleSelectBlur}
          createOptionPosition="first"
          placeholder="Search Patient..."
          loadOptions={loadOptionsForPatients}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          menuPosition="fixed"
          closeMenuOnScroll={false}
          debounceTimeout={300}
          shouldLoadMore={shouldLoadMore}
          reduceOptions={reduceOptions}
          components={SELECT_COMPONENTS}
          isPaginationLoading={isPaginationLoading}
          styles={styles}
          getOptionValue={getOptionValue}
          getOptionLabel={getLabelForAlreadyPatientOption}
          onChange={handlePatientSelect}
          formatCreateLabel={formatCreateLabel}
          isValidNewOption={isValidNewOption}
          onCreateOption={onCreateOption}
          additional={additional}
        />
      </Suspense>
    </div>
  )
}

export default PatientSearchDropdown
